import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CANAIS_CONTATO,
  ROTULO_CANAL,
  ROTULO_ESTADO_SLA,
  ROTULO_ETAPA,
  ROTULO_SCORE,
  descreverEsperaSemContato,
  formatarReais,
  type EstadoSla,
} from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { CampoCaixa, CampoSelecao, CampoTexto } from '../componentes/Campo.tsx';
import type { ItemDaFila } from '../componentes/FilaDeAtendimento.tsx';
import { CabecalhoDePagina, Medalha, Pilula, type Tom } from '../componentes/Superficie.tsx';

const TOM_DO_SLA: Record<EstadoSla, Tom> = {
  ESTOURADO: 'erro',
  EM_RISCO: 'alerta',
  NO_PRAZO: 'informacao',
  ATENDIDO: 'sucesso',
  // Nem cobrança nem elogio: o resgatado só informa de onde veio.
  RESGATADO: 'neutro',
};

/**
 * Fila de prioridade em tela cheia.
 *
 * O Sales mostra os primeiros da fila para leitura; aqui se trabalha nela: a
 * lista inteira, com o registro da tentativa de contato em cada linha.
 */
export function PaginaAtenderAgora(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [registrando, setRegistrando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const fila = useQuery({
    queryKey: ['atender-agora'],
    queryFn: () => chamarApi<ItemDaFila[]>('/atender-agora'),
  });

  const registrar = useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      chamarApi(`/oportunidades/${id}/interacoes`, { metodo: 'POST', corpo }),
    onSuccess: async () => {
      setRegistrando(null);
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['atender-agora'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['leads'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['interacoes'] });
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível registrar.');
    },
  });

  function aoRegistrar(evento: FormEvent<HTMLFormElement>, oportunidadeId: string): void {
    evento.preventDefault();
    const campos = new FormData(evento.currentTarget);
    setErro(null);
    registrar.mutate({
      id: oportunidadeId,
      corpo: {
        canal: String(campos.get('canal') ?? 'LIGACAO'),
        sentido: 'SAIDA',
        houveResposta: campos.get('houveResposta') === 'on',
        descricao: String(campos.get('descricao') ?? '') || undefined,
      },
    });
  }

  return (
    <section>
      <CabecalhoDePagina
        area="Comercial"
        titulo="Atender agora"
        descricao="Score, tempo sem primeiro contato, SLA, urgência, engajamento e etapa definem a ordem."
      />

      {erro ? (
        <p role="alert" className="mb-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      <ol className="space-y-3">
        {fila.data?.map((item, posicao) => (
          <li
            key={item.oportunidadeId}
            data-teste="item-fila"
            className="rounded-2xl border border-linha bg-superficie p-4.25 shadow-card"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-marinho text-xs font-semibold text-white">
                  {posicao + 1}
                </span>
                <div className="min-w-0">
                  <p className="truncate font-medium">{item.contato.nome}</p>
                  <p className="truncate text-sm text-texto-suave">
                    {item.produto} · {ROTULO_ETAPA[item.etapa]} ·{' '}
                    {formatarReais(item.valorMensalCentavos)} ·{' '}
                    {item.minutosSemPrimeiroContato > 0
                      ? descreverEsperaSemContato(item.minutosSemPrimeiroContato)
                      : 'Contatado'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Medalha score={item.score}>{ROTULO_SCORE[item.score]}</Medalha>
                {item.estadoSla ? (
                  <Pilula tom={TOM_DO_SLA[item.estadoSla]}>
                    {ROTULO_ESTADO_SLA[item.estadoSla]}
                  </Pilula>
                ) : null}
                <button
                  type="button"
                  onClick={() =>
                    setRegistrando((atual) =>
                      atual === item.oportunidadeId ? null : item.oportunidadeId,
                    )
                  }
                  className="rounded-[10px] bg-marinho px-3.25 py-2 text-sm font-bold text-white transition hover:bg-marinho-claro"
                >
                  Registrar contato
                </button>
              </div>
            </div>

            {registrando === item.oportunidadeId ? (
              <form
                onSubmit={(evento) => aoRegistrar(evento, item.oportunidadeId)}
                className="mt-4 grid gap-4 border-t border-linha pt-4 md:grid-cols-4"
              >
                <CampoSelecao rotulo="Canal" name="canal" defaultValue="LIGACAO">
                  {CANAIS_CONTATO.map((canal) => (
                    <option key={canal} value={canal}>
                      {ROTULO_CANAL[canal]}
                    </option>
                  ))}
                </CampoSelecao>
                <CampoTexto rotulo="Descrição" name="descricao" className="md:col-span-2" />
                <CampoCaixa rotulo="Houve resposta" name="houveResposta" className="self-end pb-2" />
                <div className="md:col-span-4">
                  <button
                    type="submit"
                    disabled={registrar.isPending}
                    className="rounded-[10px] bg-marinho px-4 py-2 text-sm font-bold text-white transition hover:bg-marinho-claro disabled:opacity-60"
                  >
                    Salvar interação
                  </button>
                </div>
              </form>
            ) : null}
          </li>
        ))}
      </ol>

      {fila.isLoading ? <p className="text-texto-suave">Carregando…</p> : null}
      {fila.data?.length === 0 ? (
        <p className="rounded-2xl border border-linha bg-superficie p-4.25 text-texto-suave shadow-card">
          Nenhuma Oportunidade aguardando atendimento.
        </p>
      ) : null}
    </section>
  );
}
